import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useToast, useUser } from "shared/hooks";

import { useAuthStore } from "../contexts";

import useLocalStorage from "../services/localStorage/localStorage";
import useAuthApiService from "../services/useAuthApiService";
import type { LoginRequest } from "../types/AuthModel";
import { ToastType } from "../types/ToastModelConfig";
import type { CreateProfileRequest } from "../types/UserModel";

const useAuth = () => {
	const authService = useAuthApiService();

	const localStorage = useLocalStorage();

	const store = useAuthStore();
	const { setState, setInitial } = useAuthStore();

	const { getProfile, getUserInfo } = useUser();

	const { showToast } = useToast();

	const { t } = useTranslation();

	const methods = useMemo(() => {
		return {
			async checkAuthStatus() {
				try {
					setState({ loading: true });
					const res = await authService.checkAuthStatus();
					return res;
				} catch (error: any) {
					console.log(error);
				} finally {
					setState({ loading: false });
				}
			},
			async login(params: { data: LoginRequest; smsLogin?: number; recaptchaToken?: string }) {
				try {
					setState({ loading: true });
					const res = await authService.login(params);
					return res?.data?.result;
				} catch (error: any) {
					console.log(error);
					return {
						loggedIn: "0"
					};
				} finally {
					setState({ loading: false });
				}
			},
			async refreshToken(refreshToken: string) {
				try {
					setState({ loading: true });
					const res = await authService.refreshToken(refreshToken);
					return res;
				} catch (error) {
					console.log(error);
				} finally {
					setState({ loading: false });
				}
			},
			async validateRegisteringProfile(params: { email: string; password: string }) {
				try {
					const validation = await authService.validateRegisteringProfile(params);
					return validation.data;
				} catch (error: any) {
					console.log(error);
					return {
						result: false,
						errors: ["Invalid"]
					};
				}
			},
			async signUp(data: Partial<CreateProfileRequest>, recaptchaToken?: string) {
				try {
					const res = await authService.signUp(data, recaptchaToken);
					return res.data;
				} catch (error) {
					console.log(error);
					return {
						result: false,
						errors: ["incorrect"]
					};
				}
			},
			async confirmEmailByCode(code: string) {
				try {
					const res = await authService.confirmEmailByCode(code);
					return res.data;
				} catch (error: any) {
					console.log(error);
					if (!!error?.errors?.length) {
						showToast({ text: error?.errors[0], noIcon: true });
					}
					return {
						result: false,
						jwtToken: "",
						refreshToken: "",
						errors: ["incorrect"]
					};
				}
			},
			async logout() {
				try {
					const res = await authService.logout();

					localStorage.clear();
					getProfile();
					getUserInfo();

					return res.data;
				} catch (error: any) {
					console.log(error);
					if (!!error?.errors?.length) {
						showToast({ text: error?.errors[0], noIcon: true });
					}
					return null;
				}
			},
			async requestPasswordReset(email: string) {
				try {
					const res = await authService.requestPasswordReset(email);

					if (res.data.result) {
						showToast({
							title: t("password_reset_success"),
							text: t("password_reset_success_desc")
						});
					}

					return res.data;
				} catch (error: any) {
					console.log(error);
					const message = !!error?.errors?.length ? error?.errors[0] : "Something went wrong!";
					if (!!error?.errors?.length) {
						showToast({ text: message, type: ToastType.ERROR });
					}
					return null;
				}
			},
			async checkResetPasswordCode(code: string) {
				try {
					setState({ checkingResetPasswordCode: true });
					const res = await authService.checkResetPasswordCode(code);
					return res.data;
				} catch (error: any) {
					console.log(error);
					if (!!error?.errors?.length) {
						showToast({ title: t("error_occurred_during_password_recovery"), text: error?.errors[0], noIcon: true });
					}
					return null;
				} finally {
					setState({ checkingResetPasswordCode: false });
				}
			},
			async resetPassword(password: string, code: string) {
				try {
					const res = await authService.resetPassword(password, code);

					if (res.data.result) {
						showToast({
							title: t("information"),
							text: t("your_password_changed_you_logged_out")
						});
					}

					return res.data;
				} catch (error: any) {
					console.log(error);
					if (!!error?.errors?.length) {
						showToast({ text: error?.errors[0], noIcon: true });
					}
					return null;
				}
			},
			async testProfile() {
				try {
					await authService.testProfile();
				} catch (error: any) {
					console.log(error);
				}
			},
			resetStore() {
				setInitial();
			}
		};
	}, [setState, authService, showToast, localStorage, getProfile, getUserInfo, setInitial, t]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useAuth;
